import { useEffect } from 'react';
import { PrismWordmark } from '@/shared/ui/prism-wordmark';

import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

import { Card, CardContent } from '@/components/ui/card';

import { useAuthStore } from '@/features/auth/model/auth-store';

import { authService } from '@/shared/api/auth-service';
import { teamService } from '@/shared/api/team-service';
import { ROUTES } from '@/shared/config/routes';
import { logger } from '@/shared/lib/logger';
import { isOnboarded } from '@/shared/lib/onboarding-flag';
import { supabase } from '@/shared/lib/supabase';
import { LoadingSpinner } from '@/shared/ui';

export const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const { login } = useAuthStore();

  useEffect(() => {
    const handleAuthCallback = async () => {
      const failureRedirect = `${ROUTES.AUTH.REGISTER}?error=callback_failed`;
      try {
        const { data, error } = await supabase.auth.getSession();

        if (error) {
          logger.auth.error('Auth callback error', { error });
          navigate(failureRedirect);
          return;
        }

        if (data.session?.user) {
          try {
            // Register profile if new user (upsert — safe for existing users too).
            // `silent: true` suppresses the global "Network error" toast that
            // the response interceptor would otherwise raise on a transient
            // failure here. The callback already owns its error UX via the
            // `?error=callback_failed` redirect below.
            await authService.registerOAuthUser(true);

            const userResponse = await authService.getMe(false, true);

            if (userResponse.type === 'success' && 'id' in userResponse.data) {
              login(userResponse.data);

              // Clean up OAuth hash params from URL
              if (window.location.hash) {
                window.history.replaceState({}, document.title, window.location.pathname);
              }

              // Check for pending invite token
              const pendingToken = sessionStorage.getItem('pendingInviteToken');
              if (pendingToken) {
                sessionStorage.removeItem('pendingInviteToken');
                try {
                  await teamService.acceptInvite(pendingToken);
                  navigate('/dashboard/team');
                  return;
                } catch {
                  // Invite acceptance failed, continue normal flow
                }
              }

              // Route the same way ProtectedRoute does: an onboarded user has
              // EITHER `onboarded_at` on the server profile OR the
              // `onboarding.completedWizard` localStorage flag (set when the
              // wizard's last step finishes). Without the flag-aware check
              // a user whose wizard PATCH never persisted (transient failure,
              // legacy account predating the column) gets bounced back into
              // /onboarding every time they sign back in.
              if (isOnboarded(userResponse.data)) {
                navigate('/dashboard');
              } else {
                navigate('/onboarding');
              }
            } else {
              navigate(failureRedirect);
            }
          } catch (apiError) {
            logger.auth.error('Failed to get user data during auth callback', { error: apiError });
            navigate(failureRedirect);
          }
        } else {
          navigate(failureRedirect);
        }
      } catch (error) {
        logger.auth.error('Auth callback error', { error });
        navigate(failureRedirect);
      }
    };

    handleAuthCallback();
  }, [navigate, login]);

  return (
    <>
      <Helmet>
        <title>Signing you in — Prism</title>
      </Helmet>
      {/* Match the visual language of /auth/register (logo + gold-glow card)
        * so users who get routed through the callback for 2-3 seconds see one
        * coherent brand surface instead of a stripped-down sibling that
        * reads as a generic "Loading…" template. */}
      <div className="flex flex-col items-center gap-6 w-full">
        <div className="flex flex-col items-center text-center">
          <PrismWordmark />
        </div>

        <Card className="w-full max-w-md border-border border-t-2 border-t-primary/30 shadow-2xl shadow-black/50 gold-glow rounded-xl">
          <CardContent className="flex flex-col items-center gap-4 py-12 px-8 text-center">
            <LoadingSpinner size="xl" color="amber" />
            <div>
              <h1 className="text-xl font-display font-semibold text-foreground">
                Signing you in
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Just a moment — setting up your workspace…
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
